import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Flex,
  Input,
  Button,
  Heading,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { react_api } from '../../../../config';

const Refine = ({ 
  visualization, 
  selectedGoal,
  currentSummary,
  username, 
  bearerToken, 
  themeColors,
  onVisualizationUpdate
}) => {
  // Local states
  const [command, setCommand] = useState('');
  const [isRefineLoading, setIsRefineLoading] = useState(false);
  const [refineError, setRefineError] = useState(null);
  const toast = useToast();

  const refineVisualization = async (e) => {
    e.preventDefault();
    
    if (!visualization || !selectedGoal || !visualization[0].code || !command.trim()) {
      toast({
        title: "Unable to modify the visualization",
        description: "Make sure there is a visualization and a command to apply",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsRefineLoading(true);
    setRefineError(null);
    
    try {
      // Prepare the payload for the repair API call
      const repairPayload = {
        username: username,
        BearerToken: bearerToken,
        service: 4,
        request: {
          code: visualization[0].code,
          feedback: command,
          library: "seaborn", 
          goal: selectedGoal,
          summary: currentSummary?.summary
        }
      };
      
      // Make the repair API call
      const repairResponse = await axios.post(`${react_api}/externalService/novaServices`, repairPayload, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const repairData = repairResponse.data;
      
      // Extract both Python code and raster image (base64) from the response
      let pythonCode = null;
      let rasterImage = null;
      
      // Check for different possible response formats for the code
      if (repairData && repairData.charts && repairData.charts.length > 0) {
        const chart = repairData.charts[0];
        if (chart.code) pythonCode = chart.code;
        if (chart.raster) rasterImage = chart.raster;
      } else if (repairData && repairData.request && repairData.request.charts && 
                repairData.request.charts.length > 0) {
        const chart = repairData.request.charts[0];
        if (chart.code) pythonCode = chart.code;
        if (chart.raster) rasterImage = chart.raster;
      } else if (repairData) {
        if (repairData.code) pythonCode = repairData.code;
        if (repairData.raster) rasterImage = repairData.raster;
      }
      
      // If we have obtained the Python code and/or raster image (base64)
      if (pythonCode || rasterImage) {
        // Create a new chart object with updated information
        const updatedChart = {
          ...visualization[0],
          code: pythonCode || visualization[0].code
        };
        
        if (rasterImage) {
          updatedChart.raster = rasterImage;
        }
        
        // Update the visualization in the parent component
        onVisualizationUpdate([updatedChart]);
        
        // Success notification
        toast({
          title: "Visualization updated",
          description: "The command has been successfully applied",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Reset the input field
        setCommand('');
      } else if (repairData && repairData.charts && repairData.charts.length > 0 && repairData.charts[0].error) {
        // Handle specific server errors
        throw new Error(repairData.charts[0].error);
      } else {
        throw new Error('Invalid response format or missing necessary data');
      }
    } catch (err) {
      console.error('Error refining visualization:', err);
      
      // Log detailed error and response for debugging
      if (err.response) {
        console.error('Error response:', err.response.data);
        console.error('Error status:', err.response.status);
        console.error('Error headers:', err.response.headers);
      }
      
      let errorMessage = 'Error refining visualization. Please try again.';
      
      if (err.response?.status === 422) {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          errorMessage = errorDetail[0]?.msg || 'Validation error';
        } else if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        }
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setRefineError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRefineLoading(false);
    }
  };

  return (
    <Box>
      {/* Interfaccia per modificare la visualizzazione */}
      <Box p={4} borderWidth="1px" borderRadius="md" bg="white" borderColor={themeColors.borderColor || '#178da1ff'}>
        <Heading as="h4" size="sm" mb={3} color={themeColors.fontColor || '#0b4e5aff'}>
          Modify Chart with Natural Language commands.
        </Heading>
        
        {/* Show an error message if present */}
        {refineError && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            <Text>{refineError}</Text>
          </Alert>
        )}
        
        {/* Form for user input */}
        <Box as="form" onSubmit={refineVisualization}>
          <Flex>
            <Box flex="1">
              <Input
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                placeholder="Change x axis label to Meters per Second"
                py={2}
                px={4}
                borderWidth="1px"
                borderRadius="md"
                isDisabled={isRefineLoading}
                _focus={{ 
                  borderColor: themeColors.borderColor || '#178da1ff', 
                  boxShadow: `0 0 0 1px ${themeColors.borderColor || '#178da1ff'}`
                }}
              />
            </Box>
            <Button
              ml={2}
              bg={themeColors.buttonColor || '#083c46ff'}
              color="white"
              _hover={{ bg: '#0a4956' }}
              type="submit"
              isLoading={isRefineLoading}
              loadingText="Applying..."
            >
              Apply
            </Button>
          </Flex>
        </Box>
        
        {/* Suggestions for commands */}
        <Box mt={4}>
          <Text fontSize="sm" color="gray.600">
            Try commands like: "Make title bigger", "Change color to blue", "Add grid lines", "Show values as percentages"
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default Refine;
